import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoginResult, UserProfile } from '../models/auth.models';
import { KeycloakTokenService } from './keycloak-token.service';
import { TokenStorageService } from './token-storage.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly loggedIn$ = new BehaviorSubject<boolean>(false);
  private readonly profile$ = new BehaviorSubject<UserProfile | null>(null);

  constructor(
    private readonly tokenStorage: TokenStorageService,
    private readonly keycloakToken: KeycloakTokenService,
    private readonly userService: UserService,
    private readonly router: Router
  ) {}

  get isLoggedIn$(): Observable<boolean> {
    return this.loggedIn$.asObservable();
  }

  get userProfile$(): Observable<UserProfile | null> {
    return this.profile$.asObservable();
  }

  isAuthenticated(): boolean {
    return this.tokenStorage.hasStoredSession() && !this.tokenStorage.isTokenExpired();
  }

  restoreSession(): Promise<void> {
    if (!this.tokenStorage.hasStoredSession()) {
      this.loggedIn$.next(false);
      this.profile$.next(null);
      return Promise.resolve();
    }

    if (this.tokenStorage.isTokenExpired()) {
      const refreshToken = this.tokenStorage.getRefreshToken();
      if (!refreshToken) {
        this.clearSession(false);
        return Promise.resolve();
      }

      return this.keycloakToken.refreshToken(refreshToken).then((tokens) => {
        if (!tokens) {
          this.clearSession(false);
          return;
        }

        this.tokenStorage.updateTokens(tokens);
        this.loggedIn$.next(true);
        return this.loadProfile(false);
      });
    }

    this.loggedIn$.next(true);
    return this.loadProfile(false);
  }

  login(username: string, password: string, rememberMe: boolean, dialCode?: string): Promise<LoginResult> {
    return this.keycloakToken.login(username, password).then(async (result) => {
      if (result.status !== 'SUCCESS' || !result.tokens) {
        return result;
      }

      this.tokenStorage.saveTokens(result.tokens, username, rememberMe, dialCode);
      this.loggedIn$.next(true);
      await this.loadProfile(rememberMe, username);
      return result;
    });
  }

  logout(navigate = true): void {
    this.clearSession(navigate);
  }

  getAccessToken(): string | null {
    return this.tokenStorage.getAccessToken();
  }

  refreshIfNeeded(): Promise<boolean> {
    if (!this.tokenStorage.isTokenExpired()) {
      return Promise.resolve(true);
    }

    const refreshToken = this.tokenStorage.getRefreshToken();
    if (!refreshToken) {
      return Promise.resolve(false);
    }

    return this.keycloakToken.refreshToken(refreshToken).then((tokens) => {
      if (!tokens) {
        this.clearSession(false);
        return false;
      }

      this.tokenStorage.updateTokens(tokens);
      return true;
    });
  }

  getDisplayName(profile: UserProfile | null): string {
    if (!profile) {
      return this.tokenStorage.getUsername() ?? '';
    }

    const fullName = [profile.fName, profile.lName].filter(Boolean).join(' ').trim();
    return fullName || profile.name || profile.phone || profile.email || 'Utilisateur';
  }

  getUserId(): string | null {
    const id = this.profile$.getValue()?.id?.trim();
    return id || null;
  }

  getUsername(): string | null {
    return this.tokenStorage.getUsername();
  }

  getProfile(): UserProfile | null {
    return this.profile$.getValue();
  }

  /** Compte activé côté backend (cache profil), aligné mobile `UserService.isActivated()`. */
  isActivated(): boolean {
    return this.getProfile()?.isActivated === true;
  }

  patchProfile(partial: Partial<UserProfile>): void {
    const current = this.profile$.getValue();
    if (!current) {
      return;
    }

    this.profile$.next(this.enrichProfile({ ...current, ...partial }));
  }

  private loadProfile(rememberProfile: boolean, username?: string): Promise<void> {
    const resolvedUsername = username ?? this.tokenStorage.getUsername();
    if (!resolvedUsername) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.userService.getProfile(resolvedUsername, rememberProfile).subscribe({
        next: (profile) => {
          this.profile$.next(this.enrichProfile(profile));
          resolve();
        },
        error: () => {
          this.profile$.next(null);
          resolve();
        },
      });
    });
  }

  private clearSession(navigate: boolean): void {
    this.tokenStorage.clear();
    this.loggedIn$.next(false);
    this.profile$.next(null);

    if (navigate) {
      this.router.navigate(['/se-connecter']);
    }
  }

  private enrichProfile(profile: UserProfile | null): UserProfile | null {
    if (!profile) {
      return profile;
    }

    const codePays = (profile.codePays || this.tokenStorage.getDialCode() || '').trim();
    if (codePays) {
      this.tokenStorage.saveDialCode(codePays);
    }

    if (codePays && codePays !== profile.codePays) {
      return { ...profile, codePays };
    }

    return profile;
  }
}
